import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LEARN_ROUTES } from '../config/learnRoutes';
import useDocumentTitle from '../hooks/useDocumentTitle';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import useTranslation from '../hooks/useTranslation';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  // Post-register target: honor a same-origin ?next= (e.g. /register?next=/enter for the
  // seed pilot), else the /learn dashboard — a new account's first action is the first
  // experience there (KAN-273 reworked flow, step 1). Relative in-app paths only.
  // KAN-491. The birth year asked at the doors, after the first challenge, travels here so
  // the account is created WITH an age on file.
  //
  // Until now the signup form sent none. `UserRegister.birth_year` has always accepted it —
  // its own comment says "the application/signup form is expected to send it" — and the form
  // never did. Two consequences followed: `create_flare_session` refused every flush with
  // "age not on file", and no under-13 check could happen at account creation at all, because
  // there was no age to check.
  //
  // Null when a learner reaches /register by another route. That is the status quo rather
  // than a new gap, and it is why the value is read defensively instead of assumed.
  const _birthYear = new URLSearchParams(window.location.search).get('birth_year');
  const birthYear = /^\d{4}$/.test(_birthYear || '') ? parseInt(_birthYear, 10) : null;

  const _next = new URLSearchParams(window.location.search).get('next');
  const postRegisterTarget = _next && _next.startsWith('/') && !_next.startsWith('//') ? _next : LEARN_ROUTES.dashboard;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useDocumentTitle('Create Account');

  // Password strength: score 0-5 based on 5 criteria
  const getPasswordStrength = (pw) => {
    if (!pw) return { score: 0, label: '', color: 'transparent', width: '0%' };
    let score = 0;
    if (pw.length >= 8)           score++;
    if (pw.length >= 12)          score++;
    if (/[A-Z]/.test(pw))         score++;
    if (/[0-9]/.test(pw))         score++;
    if (/[^A-Za-z0-9]/.test(pw))  score++;

    const levels = [
      { label: '',       color: 'transparent', width: '0%',   i18nKey: '' },
      { label: 'Weak',   color: '#DC2626',      width: '20%',  i18nKey: 'dashboard.strengthWeak' },
      { label: 'Fair',   color: '#D97706',      width: '40%',  i18nKey: 'dashboard.strengthFair' },
      { label: 'Good',   color: '#2563EB',      width: '70%',  i18nKey: 'dashboard.strengthGood' },
      { label: 'Strong', color: '#059669',      width: '90%',  i18nKey: 'dashboard.strengthStrong' },
      { label: 'Very Strong', color: '#059669', width: '100%', i18nKey: 'dashboard.strengthStrong' },
    ];
    return { score, ...levels[score] };
  };
  const passwordStrength = getPasswordStrength(formData.password);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate(postRegisterTarget);
    }
  }, [isAuthenticated, authLoading, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('auth.errors.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('auth.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.errors.emailInvalid');
    }

    if (!formData.password) {
      newErrors.password = t('auth.errors.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.errors.passwordLength');
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = t('auth.errors.passwordUppercase');
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = t('auth.errors.passwordLowercase');
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = t('auth.errors.passwordNumber');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.errors.confirmMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setServerError('');

    const result = await register(
      formData.name,
      formData.email,
      formData.password,
      formData.inviteCode || null,
      birthYear
    );

    setIsLoading(false);

    if (result.success) {
      navigate(postRegisterTarget);
    } else {
      setServerError(result.error || 'Registration failed. Please try again.');
    }
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="flex-center" style={{ minHeight: '400px' }}>
          <div className="spinner spinner-lg"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-sm">
        <div style={styles.pageHeader}>
          <h1>{t('auth.registerTitle')}</h1>
          <p>{t('auth.registerSub')}</p>
        </div>

        <div className="card" style={styles.formCard}>
          {serverError && (
            <div style={styles.errorAlert} role="alert" aria-live="assertive">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Name Field */}
            <div className="form-group">
              <label htmlFor="name">
                {t('auth.name')} <span className="required" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
                placeholder={t('auth.namePlaceholder')}
                autoFocus
                autoComplete="name"
                aria-required="true"
                aria-describedby={errors.name ? 'name-error' : undefined}
                aria-invalid={!!errors.name}
              />
              {errors.name && <div id="name-error" className="form-error" role="alert">{errors.name}</div>}
            </div>

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email">
                {t('auth.email')} <span className="required" aria-hidden="true">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
                placeholder={t('auth.emailPlaceholder')}
                autoComplete="email"
                aria-required="true"
                aria-describedby={errors.email ? 'reg-email-error' : undefined}
                aria-invalid={!!errors.email}
              />
              {errors.email && <div id="reg-email-error" className="form-error" role="alert">{errors.email}</div>}
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password">
                {t('auth.password')} <span className="required" aria-hidden="true">*</span>
              </label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  style={styles.passwordInput}
                  aria-required="true"
                  aria-describedby="password-hint password-strength-label"
                  aria-invalid={!!errors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.showPasswordBtn}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {/* Strength meter */}
              {formData.password && (
                <div style={styles.strengthMeter} aria-hidden="true">
                  <div style={styles.strengthTrack}>
                    <div style={{
                      ...styles.strengthFill,
                      width: passwordStrength.width,
                      backgroundColor: passwordStrength.color,
                    }} />
                  </div>
                  <span id="password-strength-label" style={{ ...styles.strengthLabel, color: passwordStrength.color }}>
                    {passwordStrength.i18nKey ? t(passwordStrength.i18nKey) : passwordStrength.label}
                  </span>
                </div>
              )}
              <div id="password-hint" className="form-hint">{t('auth.passwordHint')}</div>
              {errors.password && <div className="form-error" role="alert">{errors.password}</div>}
            </div>

            {/* Confirm Password Field */}
            <div className="form-group">
              <label htmlFor="confirmPassword">
                {t('auth.confirmPassword')} <span className="required" aria-hidden="true">*</span>
              </label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                  style={styles.passwordInput}
                  aria-required="true"
                  aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
                  aria-invalid={!!errors.confirmPassword}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  style={styles.showPasswordBtn}
                  aria-label={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.confirmPassword && (
                <div id="confirm-password-error" className="form-error" role="alert">{errors.confirmPassword}</div>
              )}
            </div>

            {/* Invite Code Field */}
            <div className="form-group">
              <label htmlFor="inviteCode">{t('auth.inviteCode')} <span style={{ fontWeight: 400, color: 'var(--gray-500)' }}>(Optional)</span></label>
              <input
                type="text"
                id="inviteCode"
                name="inviteCode"
                value={formData.inviteCode}
                onChange={handleChange}
                disabled={isLoading}
                placeholder={t('auth.inviteCodePlaceholder')}
                aria-describedby="invite-hint"
              />
              <div id="invite-hint" className="form-hint">
                {t('auth.inviteCodeHint')}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`btn btn-primary btn-lg ${isLoading ? 'btn-loading' : ''}`}
              disabled={isLoading}
              style={styles.submitButton}
            >
              {isLoading ? t('auth.registering') : t('auth.registerBtn')}
            </button>
          </form>

          {/* Login Link */}
          <div style={styles.footer}>
            <p>
              {t('auth.haveAccount')}{' '}
              <a href={`/login${window.location.search}`} style={styles.link}>
                {t('auth.loginLink')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles = {
  pageHeader: {
    textAlign: 'center',
    marginBottom: 'var(--spacing-2xl)',
  },
  formCard: {
    maxWidth: '500px',
    margin: '0 auto',
  },
  errorAlert: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid var(--error)',
    borderRadius: 'var(--border-radius)',
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-lg)',
    color: 'var(--error)',
    fontSize: '0.875rem',
  },
  submitButton: {
    width: '100%',
    marginTop: 'var(--spacing-lg)',
  },
  footer: {
    textAlign: 'center',
    marginTop: 'var(--spacing-lg)',
    paddingTop: 'var(--spacing-lg)',
    borderTop: '1px solid var(--gray-200)',
  },
  link: {
    color: 'var(--primary)',
    textDecoration: 'none',
    fontWeight: '600',
  },
  strengthMeter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '6px',
  },
  strengthTrack: {
    flex: 1,
    height: '4px',
    borderRadius: '2px',
    background: 'var(--gray-200)',
    overflow: 'hidden',
  },
  strengthFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.3s ease, background-color 0.3s ease',
  },
  strengthLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    minWidth: '64px',
    textAlign: 'right',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    flex: 1,
    paddingRight: '2.75rem',
  },
  showPasswordBtn: {
    position: 'absolute',
    right: '0.75rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0',
    lineHeight: '1',
    color: 'var(--gray-500)',
  },
};

export default RegisterPage;
